import React, { useState, useMemo, useCallback, memo, useEffect, useRef } from "react";
import { useIsMobile } from "@/hooks/use-mobile";
import { FixedSizeList as List } from "react-window";
import { format, parseISO } from "date-fns";
import { ptBR } from "date-fns/locale";
import { MoreHorizontal, Edit, Copy, Trash2, ChevronDown } from "lucide-react";
import { DndContext, closestCenter, KeyboardSensor, PointerSensor, useSensor, useSensors, DragEndEvent } from "@dnd-kit/core";
import { arrayMove, SortableContext, sortableKeyboardCoordinates, horizontalListSortingStrategy } from "@dnd-kit/sortable";
import { Badge } from "@/components/ui/badge";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { DropdownMenu, DropdownMenuContent, DropdownMenuItem, DropdownMenuTrigger } from "@/components/ui/dropdown-menu";
import { cn } from "@/lib/utils";
import { Transaction } from "@/context/FinanceContext";
import { ColumnVisibilitySelector, ColumnConfig } from "./ColumnVisibilitySelector";
import { ResizableColumnHeader } from "./ResizableColumnHeader";
import { ColumnFilterPopover, FilterOption } from "./ColumnFilterPopover";
import { MemoizedTransactionRow } from "./MemoizedTransactionRow";
import { currencyFormatter, formatTransactionCurrency, getSignedTransactionValue } from "@/lib/formatters";

interface Category {
  id: string;
  name: string;
  color?: string;
}

interface Account {
  id: string;
  name: string;
  color?: string;
}

interface Company {
  id: string;
  name: string;
  acronym: string;
}

interface PaymentMethod {
  id: string;
  name: string;
}

interface CostCenter {
  id: string;
  name: string;
}

interface Supplier {
  id: string;
  name: string;
}

interface OptimizedExcelTableProps {
  transactions: Transaction[];
  categories: Category[];
  accounts: Account[];
  companies?: Company[];
  paymentMethods?: PaymentMethod[];
  costCenters?: CostCenter[];
  suppliers?: Supplier[];
  selected: Set<string>;
  onSelectionChange: (selected: Set<string> | ((prev: Set<string>) => Set<string>)) => void;
  onEdit: (transaction: Transaction) => void;
  onDelete: (transaction: Transaction) => void;
  onDuplicate?: (transaction: Transaction) => void;
  onRevert?: (id: string) => void;
  onBulkEdit?: () => void;
  onBulkDelete?: () => void;
  onConfirmPayment?: () => void;
  headerType?: 'receita' | 'despesa' | 'transferencia' | 'all';
  columnFilters?: Record<string, string[]>;
  onColumnFilterChange?: (column: string, values: string[]) => void;
  onClearAllFilters?: () => void;
  onFilteredTransactionsChange?: (transactions: Transaction[]) => void;
  attachmentCounts?: Record<string, number>;
  onOpenAttachments?: (transaction: Transaction) => void;
}

type SortConfig = {
  key: string;
  direction: "asc" | "desc";
} | null;

// Default column configuration - All columns visible by default with new order
const DEFAULT_COLUMNS: ColumnConfig[] = [
  { key: "type", title: "Tipo", width: 80, visible: true },
  { key: "issueDate", title: "Emissão", width: 110, visible: true },
  { key: "dueDate", title: "Vencimento", width: 100, visible: true },
  { key: "paymentDate", title: "Pagamento", width: 100, visible: true },
  { key: "description", title: "Descrição", width: 180, visible: true },
  { key: "value", title: "Valor", width: 100, visible: true },
  { key: "parcels", title: "Parcelas", width: 80, visible: true },
  { key: "supplier", title: "Fornecedor", width: 120, visible: true },
  { key: "category", title: "Plano de Contas", width: 130, visible: true },
  { key: "account", title: "Conta Financeira", width: 110, visible: true },
  { key: "paymentMethod", title: "Forma de Pagamento", width: 100, visible: true },
  { key: "status", title: "Status", width: 80, visible: true },
  { key: "costCenter", title: "Centro Custo", width: 110, visible: true },
  { key: "attachments", title: "Comprovante", width: 100, visible: true },
];

const STORAGE_KEY = 'optimized-excel-table-columns';

// Columns to show on mobile (< 768px)
const MOBILE_VISIBLE_KEYS = new Set(["type", "description", "value", "status"]);
// Columns to show on tablet (768-1023px)
const TABLET_VISIBLE_KEYS = new Set(["type", "description", "value", "status", "dueDate", "account", "category"]);

const ITEM_HEIGHT = 45;
const HEADER_HEIGHT = 50;

// Memoized date formatter
const formatDate = (dateString: string | undefined): string => {
  if (!dateString) return "-";
  try {
    return format(parseISO(dateString), "dd/MM/yyyy", { locale: ptBR });
  } catch {
    return "-";
  }
};

// Remove the old MemoizedCell component since we're now using the optimized version from separate file

// Memoized row component using the new optimized version
const VirtualizedRow = memo<{
  index: number;
  style: React.CSSProperties;
  data: {
    transactions: Transaction[];
    visibleColumns: ColumnConfig[];
    selected: Set<string>;
    categoryById: Map<string, Category>;
    accountById: Map<string, Account>;
    paymentMethodById: Map<string, PaymentMethod>;
    costCenterById: Map<string, CostCenter>;
    supplierById: Map<string, Supplier>;
    onSelectTransaction: (id: string, checked: boolean) => void;
    attachmentCounts: Record<string, number>;
    onOpenAttachments?: (transaction: Transaction) => void;
  };
}>(({ index, style, data }) => {
  const transaction = data.transactions[index];
  
  return (
    <MemoizedTransactionRow
      index={index}
      style={style}
      transaction={transaction}
      visibleColumns={data.visibleColumns}
      selected={data.selected}
      categoryById={data.categoryById}
      accountById={data.accountById}
      paymentMethodById={data.paymentMethodById}
      costCenterById={data.costCenterById}
      supplierById={data.supplierById}
      onSelectTransaction={data.onSelectTransaction}
      attachmentCount={data.attachmentCounts[transaction?.id] || 0}
      onOpenAttachments={data.onOpenAttachments}
    />
  );
});

VirtualizedRow.displayName = "VirtualizedRow";

export const OptimizedExcelTable: React.FC<OptimizedExcelTableProps> = ({
  transactions,
  categories,
  accounts,
  companies = [],
  paymentMethods = [],
  costCenters = [],
  suppliers = [],
  selected,
  onSelectionChange,
  onEdit,
  onDelete,
  onDuplicate,
  onRevert,
  onBulkEdit,
  onBulkDelete,
  onConfirmPayment,
  columnFilters = {},
  onColumnFilterChange,
  onClearAllFilters,
  onFilteredTransactionsChange,
  attachmentCounts = {},
  onOpenAttachments,
}) => {
  const isMobile = useIsMobile();
  const [isTablet, setIsTablet] = useState(false);
  useEffect(() => {
    const mql = window.matchMedia('(min-width: 768px) and (max-width: 1023px)');
    const onChange = () => setIsTablet(mql.matches);
    mql.addEventListener('change', onChange);
    setIsTablet(mql.matches);
    return () => mql.removeEventListener('change', onChange);
  }, []);

  const [sortConfig, setSortConfig] = useState<SortConfig>(null);
  const [columns, setColumns] = useState<ColumnConfig[]>(() => {
    // Load saved columns from localStorage or use defaults
    try {
      const saved = localStorage.getItem(STORAGE_KEY);
      if (saved) {
        const savedColumns = JSON.parse(saved);
        return DEFAULT_COLUMNS.map(defaultCol => {
          const savedCol = savedColumns.find((col: ColumnConfig) => col.key === defaultCol.key);
          if (savedCol) {
            return {
              ...defaultCol,
              width: savedCol.width,
              visible: savedCol.visible
            };
          }
          return defaultCol;
        }).filter(col => col.key !== 'actions');
      }
    } catch (error) {
      console.warn('Failed to load column configuration from localStorage:', error);
    }
    return DEFAULT_COLUMNS.filter(col => col.key !== 'actions');
  });
  const [hasAppliedInitialAutoResize, setHasAppliedInitialAutoResize] = useState(false);
  const [containerWidth, setContainerWidth] = useState(0);
  const tableContainerRef = useRef<HTMLDivElement>(null);
  
  // Cached canvas for text measurement - avoids creating DOM elements on every call
  const measureCanvasRef = useRef<HTMLCanvasElement | null>(null);
  const getMeasureContext = useCallback(() => {
    if (!measureCanvasRef.current) {
      measureCanvasRef.current = document.createElement('canvas');
    }
    const ctx = measureCanvasRef.current.getContext('2d');
    if (ctx) {
      ctx.font = '14px system-ui, -apple-system, sans-serif';
    }
    return ctx;
  }, []);

  // Save columns to localStorage whenever they change
  useEffect(() => {
    try {
      localStorage.setItem(STORAGE_KEY, JSON.stringify(columns));
    } catch (error) {
      console.warn('Failed to save column configuration to localStorage:', error);
    }
  }, [columns]);

  const sensors = useSensors(
    useSensor(PointerSensor),
    useSensor(KeyboardSensor, {
      coordinateGetter: sortableKeyboardCoordinates,
    })
  );

  // Memoized lookup maps
  const categoryById = useMemo(() => new Map(categories.map((c) => [c.id, c])), [categories]);
  const accountById = useMemo(() => new Map(accounts.map((a) => [a.id, a])), [accounts]);
  const paymentMethodById = useMemo(() => new Map((paymentMethods || []).map((p) => [p.id, p])), [paymentMethods]);
  const costCenterById = useMemo(() => new Map((costCenters || []).map((c) => [c.id, c])), [costCenters]);
  const supplierById = useMemo(() => new Map((suppliers || []).map((s) => [s.id, s])), [suppliers]);

  // Track container width via ResizeObserver
  useEffect(() => {
    const el = tableContainerRef.current;
    if (!el) return;
    const ro = new ResizeObserver((entries) => {
      for (const entry of entries) {
        setContainerWidth(entry.contentRect.width);
      }
    });
    ro.observe(el);
    setContainerWidth(el.clientWidth);
    return () => ro.disconnect();
  }, []);

  // Get visible columns - on mobile/tablet, override to show fewer columns
  const visibleColumns = useMemo(() => {
    const userVisible = columns.filter(col => col.visible);
    if (isMobile) {
      return userVisible.filter(col => MOBILE_VISIBLE_KEYS.has(col.key));
    }
    if (isTablet) {
      return userVisible.filter(col => TABLET_VISIBLE_KEYS.has(col.key));
    }
    return userVisible;
  }, [columns, isMobile, isTablet]);

  // Compute effective columns that ALWAYS fit the container exactly
  const CHECKBOX_WIDTH = 48;
  const MIN_COL_WIDTH = 50;
  const effectiveColumns = useMemo(() => {
    if (!containerWidth || visibleColumns.length === 0) return visibleColumns;
    
    // On mobile/tablet, don't scale — use natural widths for horizontal scroll
    if (isMobile || isTablet) {
      return visibleColumns;
    }
    
    const availableWidth = containerWidth - CHECKBOX_WIDTH;
    const rawTotal = visibleColumns.reduce((s, c) => s + c.width, 0);
    if (rawTotal <= availableWidth) {
      const scale = availableWidth / rawTotal;
      let distributed = 0;
      return visibleColumns.map((col, i) => {
        if (i === visibleColumns.length - 1) {
          return { ...col, width: availableWidth - distributed };
        }
        const w = Math.max(MIN_COL_WIDTH, Math.floor(col.width * scale));
        distributed += w;
        return { ...col, width: w };
      });
    }
    const scale = availableWidth / rawTotal;
    let distributed = 0;
    return visibleColumns.map((col, i) => {
      if (i === visibleColumns.length - 1) {
        return { ...col, width: Math.max(MIN_COL_WIDTH, availableWidth - distributed) };
      }
      const w = Math.max(MIN_COL_WIDTH, Math.floor(col.width * scale));
      distributed += w;
      return { ...col, width: w };
    });
  }, [visibleColumns, containerWidth, isMobile, isTablet]);

  // Calculate total columns width for synchronized scrolling
  const totalColumnsWidth = useMemo(() => {
    return effectiveColumns.reduce((sum, col) => sum + col.width, 0);
  }, [effectiveColumns]);

// Helper function to get transaction status (3 estados)
const getTransactionStatus = (transaction: Transaction): string => {
  if (transaction.reconciled === true) return "Conciliado";
  if (transaction.settled !== false) return "Pago";
  
  if (transaction.dueDate) {
    const today = new Date();
    const dueDate = new Date(transaction.dueDate);
    today.setHours(0, 0, 0, 0);
    dueDate.setHours(0, 0, 0, 0);
    
    if (dueDate < today) {
      return "Vencido";
    }
  }
  
  return "Em Aberto";
};

// Helper function to check if a date is within a range
const isDateInRange = (dateString: string | undefined, rangeFilter: any): boolean => {
  if (!dateString || !rangeFilter) return false;
  
  const date = new Date(dateString);
  const startDate = rangeFilter.start ? new Date(rangeFilter.start) : null;
  const endDate = rangeFilter.end ? new Date(rangeFilter.end) : null;
  
  if (startDate && endDate) {
    return date >= startDate && date <= endDate;
  } else if (startDate) {
    return date >= startDate;
  } else if (endDate) {
    return date <= endDate;
  }
  
  return false;
};

// Optimized filter transactions with cached lookups
  const filteredTransactions = useMemo(() => {
    let result = transactions;
    
    if (onColumnFilterChange && Object.keys(columnFilters).length > 0) {
      // Pre-compute all filter values for better performance
      const filterEntries = Object.entries(columnFilters).filter(([, filterValues]) => 
        filterValues && filterValues.length > 0
      );

      if (filterEntries.length > 0) {
        result = transactions.filter(transaction => {
      return filterEntries.every(([columnKey, filterValues]) => {
        // Check if this is a date range filter
        if (filterValues.length === 1 && filterValues[0].startsWith('{"type":"dateRange"')) {
          try {
            const rangeFilter = JSON.parse(filterValues[0]);
            switch (columnKey) {
              case "issueDate":
                return isDateInRange(transaction.issueDate, rangeFilter);
              case "dueDate":
                return isDateInRange(transaction.dueDate, rangeFilter);
              case "paymentDate":
                return isDateInRange(transaction.paymentDate, rangeFilter);
              default:
                return true;
            }
          } catch {
            return true;
          }
        }

        // Regular filter logic for non-date fields
        let value = "";
        
         // Optimized switch with direct lookups
         switch (columnKey) {
           case "issueDate":
             // Use formatted date for consistency with filter options
             value = transaction.issueDate ? formatDate(transaction.issueDate) : "";
             break;
           case "dueDate":
             // Use formatted date for consistency with filter options
             value = transaction.dueDate ? formatDate(transaction.dueDate) : "";
             break;
           case "paymentDate":
             // Use formatted date for consistency with filter options
             value = transaction.paymentDate ? formatDate(transaction.paymentDate) : "";
             break;
          case "description":
            value = transaction.description || "";
            break;
          case "value":
             value = formatTransactionCurrency(transaction.value, transaction.type);
            break;
          case "category":
            value = categoryById.get(transaction.categoryId)?.name || "-";
            break;
          case "account":
            value = accountById.get(transaction.accountId)?.name || "-";
            break;
          case "supplier":
            value = supplierById.get(transaction.supplierId || "")?.name || "-";
            break;
          case "type":
            // Considerar transferências como tipo separado
            if (transaction.kind === "transferencia") {
              value = "Transferência";
            } else {
              value = transaction.type === "receita" ? "Receita" : "Despesa";
            }
            break;
          case "status":
            value = getTransactionStatus(transaction);
            break;
           case "paymentMethod":
             value = paymentMethodById.get(transaction.paymentMethodId || "")?.name || "-";
             break;
           case "costCenter":
             value = costCenterById.get(transaction.costCenterId || "")?.name || "-";
             break;
          case "parcels":
            value = transaction.parcels ? `${transaction.parcelIndex}/${transaction.parcels}` : "-";
            break;
          default:
            return true;
        }

        return filterValues.includes(value);
        });
      });
      }
    }
    
    return result;
  }, [transactions, columnFilters, onColumnFilterChange, categoryById, accountById, paymentMethodById, costCenterById, supplierById]);

  // Notify parent component of filtered transactions
  useEffect(() => {
    if (onFilteredTransactionsChange) {
      onFilteredTransactionsChange(filteredTransactions);
    }
  }, [filteredTransactions, onFilteredTransactionsChange]);

  // Optimized sorting with early returns
  const sortedTransactions = useMemo(() => {
    if (!sortConfig || filteredTransactions.length === 0) return filteredTransactions;

    return [...filteredTransactions].sort((a, b) => {
      let aVal: any = "";
      let bVal: any = "";

      switch (sortConfig.key) {
        case "account":
          aVal = accountById.get(a.accountId)?.name || "";
          bVal = accountById.get(b.accountId)?.name || "";
          break;
        case "category":
          aVal = categoryById.get(a.categoryId)?.name || "";
          bVal = categoryById.get(b.categoryId)?.name || "";
          break;
          break;
        case "paymentMethod":
          aVal = paymentMethodById.get(a.paymentMethodId || "")?.name || "";
          bVal = paymentMethodById.get(b.paymentMethodId || "")?.name || "";
          break;
        case "costCenter":
          aVal = costCenterById.get(a.costCenterId || "")?.name || "";
          bVal = costCenterById.get(b.costCenterId || "")?.name || "";
          break;
        case "supplier":
          aVal = supplierById.get(a.supplierId || "")?.name || "";
          bVal = supplierById.get(b.supplierId || "")?.name || "";
          break;
        case "parcels":
          aVal = a.parcels ? `${a.parcelIndex}/${a.parcels}` : "-";
          bVal = b.parcels ? `${b.parcelIndex}/${b.parcels}` : "-";
          break;
        case "status":
          aVal = getTransactionStatus(a);
          bVal = getTransactionStatus(b);
          break;
        case "type":
          aVal = a.kind === "transferencia" ? "Transferência" : (a.type === "receita" ? "Receita" : "Despesa");
          bVal = b.kind === "transferencia" ? "Transferência" : (b.type === "receita" ? "Receita" : "Despesa");
          break;
        case "value":
          aVal = getSignedTransactionValue(a.value, a.type);
          bVal = getSignedTransactionValue(b.value, b.type);
          break;
        default:
          aVal = a[sortConfig.key as keyof Transaction] || "";
          bVal = b[sortConfig.key as keyof Transaction] || "";
      }

      if (aVal < bVal) return sortConfig.direction === "asc" ? -1 : 1;
      if (aVal > bVal) return sortConfig.direction === "asc" ? 1 : -1;
      return 0;
    });
  }, [filteredTransactions, sortConfig, accountById, categoryById, paymentMethodById, costCenterById, supplierById]);

  const handleSort = useCallback((key: string) => {
    setSortConfig((current) => {
      if (current?.key === key) {
        return { key, direction: current.direction === "asc" ? "desc" : "asc" };
      }
      return { key, direction: "asc" };
    });
  }, []);

  const handleColumnReorder = useCallback((event: DragEndEvent) => {
    const { active, over } = event;
    
    if (over && active.id !== over.id) {
      setColumns((prevColumns) => {
        const oldIndex = prevColumns.findIndex((col) => col.key === active.id);
        const newIndex = prevColumns.findIndex((col) => col.key === over.id);
        
        return arrayMove(prevColumns, oldIndex, newIndex);
      });
    }
  }, []);

  const handleColumnVisibilityChange = useCallback((columnKey: string, visible: boolean) => {
    setColumns(prev => prev.map(col => 
      col.key === columnKey ? { ...col, visible } : col
    ));
  }, []);

  const handleSelectAllColumns = useCallback(() => {
    setColumns(prev => prev.map(col => ({ ...col, visible: true })));
  }, []);

  const handleSelectNoColumns = useCallback(() => {
    setColumns(prev => prev.map(col => ({ ...col, visible: false })));
  }, []);

  const handleColumnResize = useCallback((columnKey: string, newWidth: number) => {
    const MIN_COL_WIDTH = 50;
    setColumns(prev => prev.map(col => 
      col.key === columnKey ? { ...col, width: Math.max(MIN_COL_WIDTH, newWidth) } : col
    ));
  }, []);

  // Helper function to calculate minimum column width based on content
  const calculateMinColumnWidth = useCallback((columnKey: string): number => {
    const column = columns.find(col => col.key === columnKey);
    if (!column) return 100;

    let maxWidth = 80; // minimum width
    
    // Use cached canvas context instead of creating new DOM element
    const context = getMeasureContext();
    if (context) {
      const titleWidth = context.measureText(column.title).width + 60; // Add padding for icons and spacing
      maxWidth = Math.max(maxWidth, titleWidth);
    }

    // Sample some transactions to estimate content width
    const sampleSize = Math.min(50, sortedTransactions.length);
    for (let i = 0; i < sampleSize; i++) {
      const transaction = sortedTransactions[i];
      let cellValue = "";
      
      switch (columnKey) {
        case "issueDate":
          cellValue = formatDate(transaction.issueDate);
          break;
        case "description":
          cellValue = transaction.description;
          break;
        case "value":
          cellValue = formatTransactionCurrency(transaction.value, transaction.type);
          break;
        case "category":
          cellValue = categoryById.get(transaction.categoryId)?.name || "N/A";
          break;
        case "account":
          cellValue = accountById.get(transaction.accountId)?.name || "N/A";
          break;
        case "supplier":
          cellValue = supplierById.get(transaction.supplierId || "")?.name || "-";
          break;
        case "paymentMethod":
          cellValue = paymentMethodById.get(transaction.paymentMethodId || "")?.name || "-";
          break;
        case "costCenter":
          cellValue = costCenterById.get(transaction.costCenterId || "")?.name || "-";
          break;
        case "parcels":
          cellValue = transaction.parcels ? `${transaction.parcelIndex}/${transaction.parcels}` : "-";
          break;
        default:
          cellValue = String(transaction[columnKey as keyof Transaction] || "");
      }
      
      if (context) {
        const cellWidth = context.measureText(cellValue).width + 32; // Add padding
        maxWidth = Math.max(maxWidth, cellWidth);
      }
    }

    // Cap the width to reasonable limits but ensure header is always fully visible
    return Math.min(Math.max(maxWidth, 100), 400);
  }, [columns, sortedTransactions, categoryById, accountById, paymentMethodById, costCenterById, supplierById, getMeasureContext]);

  const handleAutoResize = useCallback((columnKey: string) => {
    const finalWidth = calculateMinColumnWidth(columnKey);
    handleColumnResize(columnKey, finalWidth);
  }, [calculateMinColumnWidth, handleColumnResize]);

  const handleAutoResizeAll = useCallback(() => {
    if (!tableContainerRef.current) return;
    
    const containerWidth = tableContainerRef.current.clientWidth;
    const checkboxWidth = 48; // w-12 = 48px
    const availableWidth = containerWidth - checkboxWidth;
    
    if (visibleColumns.length === 0) return;
    
    // Calculate minimum width needed for each visible column
    const minWidths: Record<string, number> = {};
    let totalMinWidth = 0;
    
    visibleColumns.forEach((column) => {
      const minWidth = calculateMinColumnWidth(column.key);
      minWidths[column.key] = minWidth;
      totalMinWidth += minWidth;
    });
    
    // Scale factor: always scale to fit exactly in availableWidth
    const scale = availableWidth / totalMinWidth;
    const MIN_COL_WIDTH = 60;
    
    // First pass: calculate floored widths
    const newWidths: Record<string, number> = {};
    let totalScaled = 0;
    
    visibleColumns.forEach((col) => {
      const w = Math.max(MIN_COL_WIDTH, Math.floor(minWidths[col.key] * scale));
      newWidths[col.key] = w;
      totalScaled += w;
    });
    
    // Second pass: distribute remaining pixels to match exactly
    let remainder = availableWidth - totalScaled;
    const visKeys = visibleColumns.map(c => c.key);
    let i = 0;
    while (remainder > 0 && i < visKeys.length) {
      newWidths[visKeys[i]] += 1;
      remainder--;
      i++;
    }
    // If remainder is negative (due to MIN_COL_WIDTH clamping), shrink widest columns
    while (remainder < 0) {
      let maxKey = visKeys[0];
      for (const k of visKeys) {
        if (newWidths[k] > newWidths[maxKey]) maxKey = k;
      }
      if (newWidths[maxKey] <= MIN_COL_WIDTH) break;
      newWidths[maxKey] -= 1;
      remainder++;
    }
    
    setColumns(prev => prev.map(col => {
      if (!col.visible) return col;
      return { ...col, width: newWidths[col.key] ?? col.width };
    }));
  }, [visibleColumns, calculateMinColumnWidth]);

  // Apply initial auto-resize on first load
  useEffect(() => {
    if (!hasAppliedInitialAutoResize && transactions.length > 0 && visibleColumns.length > 0 && tableContainerRef.current) {
      // Apply auto-resize to all visible columns on first load
      setTimeout(() => {
        handleAutoResizeAll();
        setHasAppliedInitialAutoResize(true);
      }, 100); // Small delay to ensure rendering is complete
    }
  }, [hasAppliedInitialAutoResize, transactions.length, visibleColumns.length, handleAutoResizeAll]);

  const handleSelectAll = useCallback((checked: boolean) => {
    if (checked) {
      const allIds = sortedTransactions.map(t => t.id);
      onSelectionChange(() => new Set(allIds));
    } else {
      onSelectionChange(() => new Set());
    }
  }, [sortedTransactions, onSelectionChange]);

  const handleSelectTransaction = useCallback((id: string, checked: boolean) => {
    onSelectionChange((prevSelected: Set<string>) => {
      const newSelected = new Set(prevSelected);
      if (checked) {
        newSelected.add(id);
      } else {
        newSelected.delete(id);
      }
      return newSelected;
    });
  }, [onSelectionChange]);

  const isAllSelected = selected.size > 0 && selected.size === sortedTransactions.length;

  // Helper to resolve a transaction's display value for a given column key
  const getTransactionColumnValue = useCallback((transaction: Transaction, columnKey: string): string => {
    switch (columnKey) {
      case "issueDate":
        return transaction.issueDate ? formatDate(transaction.issueDate) : "";
      case "dueDate":
        return transaction.dueDate ? formatDate(transaction.dueDate) : "";
      case "paymentDate":
        return transaction.paymentDate ? formatDate(transaction.paymentDate) : "";
      case "description":
        return transaction.description || "";
      case "value":
        return formatTransactionCurrency(transaction.value, transaction.type);
      case "category":
        return categoryById.get(transaction.categoryId)?.name || "-";
      case "account":
        return accountById.get(transaction.accountId)?.name || "-";
      case "supplier":
        return supplierById.get(transaction.supplierId || "")?.name || "-";
      case "type":
        if (transaction.kind === "transferencia") return "Transferência";
        return transaction.type === "receita" ? "Receita" : "Despesa";
      case "status":
        return getTransactionStatus(transaction);
      case "paymentMethod":
        return paymentMethodById.get(transaction.paymentMethodId || "")?.name || "-";
      case "costCenter":
        return costCenterById.get(transaction.costCenterId || "")?.name || "-";
      case "parcels":
        return transaction.parcels ? `${transaction.parcelIndex}/${transaction.parcels}` : "-";
      default:
        return "";
    }
  }, [categoryById, accountById, paymentMethodById, costCenterById, supplierById]);

  // Memoized filter options cache with cross-column filtering
  const filterOptionsCache = useMemo(() => {
    const cache: Record<string, FilterOption[]> = {};
    
    const activeFilterEntries = Object.entries(columnFilters).filter(([, vals]) => vals && vals.length > 0);

    const getOptions = (column: string): FilterOption[] => {
      const valueCountMap = new Map<string, number>();
      
      // Apply all filters EXCEPT the current column's filter
      const otherFilters = activeFilterEntries.filter(([key]) => key !== column);
      
      const baseTransactions = otherFilters.length > 0
        ? transactions.filter(transaction =>
            otherFilters.every(([colKey, filterValues]) => {
              // Date range filter check
              if (filterValues.length === 1 && filterValues[0].startsWith('{"type":"dateRange"')) {
                try {
                  const rangeFilter = JSON.parse(filterValues[0]);
                  switch (colKey) {
                    case "issueDate": return isDateInRange(transaction.issueDate, rangeFilter);
                    case "dueDate": return isDateInRange(transaction.dueDate, rangeFilter);
                    case "paymentDate": return isDateInRange(transaction.paymentDate, rangeFilter);
                    default: return true;
                  }
                } catch { return true; }
              }
              return filterValues.includes(getTransactionColumnValue(transaction, colKey));
            })
          )
        : transactions;

      for (const transaction of baseTransactions) {
        const value = getTransactionColumnValue(transaction, column);
        if (value) {
          valueCountMap.set(value, (valueCountMap.get(value) || 0) + 1);
        }
      }

      const dateColumns = ['issueDate', 'dueDate', 'paymentDate'];

      const parseDateForSort = (dateStr: string): number => {
        const parts = dateStr.split('/');
        if (parts.length === 3) {
          const [day, month, year] = parts;
          return new Date(Number(year), Number(month) - 1, Number(day)).getTime();
        }
        return 0;
      };

      const parseValueForSort = (valueStr: string): number => {
        const cleaned = valueStr
          .replace(/R\$\s?/, '')
          .replace(/\./g, '')
          .replace(',', '.');
        return parseFloat(cleaned) || 0;
      };

      return Array.from(valueCountMap.entries())
        .sort(([a], [b]) => {
          if (dateColumns.includes(column)) {
            return parseDateForSort(a) - parseDateForSort(b);
          }
          if (column === 'value') {
            return parseValueForSort(a) - parseValueForSort(b);
          }
          return a.localeCompare(b);
        })
        .map(([value, count]) => ({ value, label: value, count }));
    };

    for (const { key } of visibleColumns) {
      cache[key] = getOptions(key);
    }
    
    return cache;
  }, [transactions, columnFilters, categoryById, accountById, paymentMethodById, costCenterById, supplierById, visibleColumns, getTransactionColumnValue, isDateInRange]);

  const getFilterOptions = useCallback((column: string): FilterOption[] => {
    return filterOptionsCache[column] || [];
  }, [filterOptionsCache]);

  // Memoized data for virtualized list
  const listData = useMemo(() => ({
    transactions: sortedTransactions,
    visibleColumns: effectiveColumns,
    selected,
    categoryById,
    accountById,
    paymentMethodById,
    costCenterById,
    supplierById,
    onSelectTransaction: handleSelectTransaction,
    attachmentCounts,
    onOpenAttachments
  }), [
    sortedTransactions,
    effectiveColumns,
    selected,
    categoryById,
    accountById,
    paymentMethodById,
    costCenterById,
    supplierById,
    handleSelectTransaction,
    attachmentCounts,
    onOpenAttachments
  ]);

  if (sortedTransactions.length === 0) {
    return (
      <div className="h-96 border rounded-lg bg-background flex items-center justify-center">
        <p className="text-muted-foreground">Nenhum lançamento encontrado</p>
      </div>
    );
  }

  // For mobile/tablet, show the effective visible count vs total
  const displayVisibleCount = visibleColumns.length;
  const displayTotalCount = columns.length;

  return (
    <div className="w-full space-y-4">
      {/* Column Controls */}
      <div className="flex justify-end gap-2">
        {!isMobile && (
          <Button
            variant="outline"
            size="sm"
            onClick={handleAutoResizeAll}
            className="text-xs"
          >
            Ajustar Colunas
          </Button>
        )}
        {onClearAllFilters && (
          <Button
            variant="outline"
            size="sm"
            onClick={onClearAllFilters}
            className="text-xs"
          >
            {isMobile ? "Limpar" : "Limpar Filtros"}
          </Button>
        )}
        <ColumnVisibilitySelector
          columns={columns}
          onColumnVisibilityChange={handleColumnVisibilityChange}
          onSelectAll={handleSelectAllColumns}
          onSelectNone={handleSelectNoColumns}
          visibleOverrideCount={isMobile || isTablet ? displayVisibleCount : undefined}
        />
      </div>

      {/* Table */}
      <div ref={tableContainerRef} className="w-full border rounded-lg bg-background overflow-hidden">
        {/* Allow horizontal scroll on mobile/tablet */}
        <div className={cn("overflow-y-hidden", (isMobile || isTablet) ? "overflow-x-auto -webkit-overflow-scrolling-touch" : "overflow-x-hidden")}>
          <div style={{ minWidth: (isMobile || isTablet) ? `${visibleColumns.reduce((s, c) => s + c.width, 0) + CHECKBOX_WIDTH}px` : '100%', width: (isMobile || isTablet) ? undefined : '100%' }}>
            <DndContext
              sensors={sensors}
              collisionDetection={closestCenter}
              onDragEnd={handleColumnReorder}
            >
              {/* Header */}
              <div 
                className="flex border-b bg-gray-100"
                style={{ 
                  height: HEADER_HEIGHT,
                  width: '100%'
                }}
              >
                <div className="w-12 flex items-center justify-center border-r border-gray-200 flex-shrink-0 h-full">
                  <Checkbox
                    checked={isAllSelected}
                    onCheckedChange={handleSelectAll}
                    className="border-gray-400 data-[state=checked]:bg-black data-[state=checked]:text-white"
                  />
                </div>
                
                <SortableContext 
                  items={effectiveColumns.map(col => col.key)} 
                  strategy={horizontalListSortingStrategy}
                >
                  {effectiveColumns.map((column) => {
                    const hasActiveFilters = (columnFilters[column.key]?.length || 0) > 0;
                     
                    const handleResizeMouseDown = (e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      const startX = e.clientX;
                      const startWidth = column.width;

                      const handleMouseMove = (moveEvent: MouseEvent) => {
                        const diff = moveEvent.clientX - startX;
                        const adjustedDiff = diff * 0.5;
                        const newWidth = Math.max(50, startWidth + adjustedDiff);
                        handleColumnResize(column.key, newWidth);
                      };

                      const handleMouseUp = () => {
                        document.removeEventListener("mousemove", handleMouseMove);
                        document.removeEventListener("mouseup", handleMouseUp);
                      };

                      document.addEventListener("mousemove", handleMouseMove);
                      document.addEventListener("mouseup", handleMouseUp);
                    };

                    const handleResizeDoubleClick = (e: React.MouseEvent) => {
                      e.preventDefault();
                      e.stopPropagation();
                      handleAutoResize(column.key);
                    };
                     
                    return (
                      <div key={column.key} className="relative h-full group overflow-hidden" style={{ width: `${column.width}px` }}>
                        <div className="flex items-center h-full overflow-hidden min-w-0">
                          <ResizableColumnHeader
                            id={column.key}
                            title={column.title}
                            width={column.width}
                            sortDirection={sortConfig?.key === column.key ? sortConfig.direction : null}
                            onSort={() => handleSort(column.key)}
                            onResize={(newWidth) => handleColumnResize(column.key, newWidth)}
                            onAutoResize={() => handleAutoResize(column.key)}
                          />
                          
                          {onColumnFilterChange && column.key !== "attachments" && (
                            <ColumnFilterPopover
                              title={`Filtrar ${column.title}`}
                              options={getFilterOptions(column.key)}
                              selected={columnFilters[column.key] || []}
                              onSelectionChange={(values) => onColumnFilterChange(column.key, values)}
                              searchPlaceholder={`Pesquisar ${column.title.toLowerCase()}...`}
                            >
                              <button
                                className={cn(
                                  "flex items-center justify-center w-8 h-8 rounded-md border transition-all flex-shrink-0 relative mr-2",
                                  "hover:shadow-md focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-1",
                                  hasActiveFilters
                                    ? "bg-blue-50 border-blue-400 hover:bg-blue-100"
                                    : "bg-white border-gray-300 hover:bg-gray-50 hover:border-gray-400 shadow-sm"
                                )}
                                aria-label={`Filtrar coluna ${column.title}`}
                                aria-pressed={hasActiveFilters}
                                aria-haspopup="dialog"
                                tabIndex={0}
                              >
                                <ChevronDown className="w-4 h-4 text-gray-700" />
                                {hasActiveFilters && (
                                  <div className="absolute -top-1 -right-1 w-2.5 h-2.5 bg-blue-600 rounded-full border-2 border-white shadow-sm" />
                                )}
                              </button>
                            </ColumnFilterPopover>
                          )}
                        </div>
                        
                        {/* Resize Handle na extremidade direita */}
                        <div
                          className="absolute right-0 top-0 bottom-0 w-2 cursor-col-resize hover:bg-gray-300 transition-colors z-20"
                          onMouseDown={handleResizeMouseDown}
                          onDoubleClick={handleResizeDoubleClick}
                          title="Arraste para redimensionar ou clique duplo para ajuste automático"
                        />
                        
                        {/* Borda direita da coluna */}
                        <div className="absolute right-0 top-0 bottom-0 w-px bg-gray-200 pointer-events-none" />
                      </div>
                    );
                  })}
                </SortableContext>
              </div>
            </DndContext>

            {/* Virtualized Body */}
            <div style={{ height: `${Math.min(sortedTransactions.length * ITEM_HEIGHT + 20, 600)}px` }}>
              <List
                height={Math.min(sortedTransactions.length * ITEM_HEIGHT + 20, 600)}
                width={containerWidth || tableContainerRef.current?.clientWidth || totalColumnsWidth + CHECKBOX_WIDTH}
                itemCount={sortedTransactions.length}
                itemSize={ITEM_HEIGHT}
                itemData={listData}
                overscanCount={10}
              >
                {VirtualizedRow}
              </List>
            </div>

            {/* Subtotal Footer */}
            <div 
              className="bg-gray-100 text-black border-t border-gray-200"
              style={{ width: '100%' }}
            >
              <div className="flex items-center h-12">
                <div className="w-12 flex-shrink-0"></div>
                {effectiveColumns.map((column, index) => (
                  <div
                    key={column.key}
                    className={cn(
                      "flex items-center font-semibold text-sm flex-shrink-0",
                      column.key === "value" ? "justify-center" : "justify-start"
                    )}
                    style={{ 
                      width: column.width,
                      minWidth: column.width,
                      maxWidth: column.width 
                    }}
                  >
                    {column.key === "value" && (
                      <span className="w-full text-center">
                        {currencyFormatter.format(
                          sortedTransactions.reduce((sum, transaction) => {
                            return sum + (transaction.type === "receita" ? transaction.value : -transaction.value);
                          }, 0)
                        )}
                      </span>
                    )}
                    {column.key === "description" && index === 0 && (
                      <span>Total ({sortedTransactions.length} lançamentos)</span>
                    )}
                  </div>
                ))}
              </div>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
};